import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from './config';
import ComplicDashboard from './components/ComplicDashboard';
import Agents from './components/Agents';
import Settings from './components/SettingsNew';
import Worklog from './components/Worklog';
import Navbar from './components/Navbar';
import Login from './components/Login';
import FloatingComplic from './components/FloatingComplic';
import ComplicChat from './components/ComplicChat';
import FloatingVoiceAssistant from './components/FloatingVoiceAssistant';
import CodePanel from './components/CodePanel';
import BrowserPanel from './components/BrowserPanel';
import CodeToggleHandle from './components/CodeToggleHandle';
import JiraRateLimitBanner from './components/JiraRateLimitBanner';
import { ThemeProvider } from './context/ThemeContext';
import { VoiceProvider } from './context/VoiceContext';
import { ProjectProvider, useProject } from './context/ProjectContext';
import { applyAuthToken, clearAuthSession } from './api/authSession';


type PageType = 'dashboard' | 'tickets' | 'agents' | 'jobs' | 'worklog' | 'settings';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [pendingTicketOpen, setPendingTicketOpen] = useState<{
    ticketKey: string;
    section?: string | null;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Load last active page from localStorage (Tickets merged into Worklog)
  const [currentPage, setCurrentPage] = useState<PageType>(() => {
    const saved = localStorage.getItem('complic_current_page');
    if (saved === 'tickets' || saved === 'jobs') {
      return saved === 'jobs' ? 'agents' : 'worklog';
    }
    return (saved as PageType) || 'worklog';
  });

  // State for page transitions
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [animationClass, setAnimationClass] = useState('');
  const [previousPage, setPreviousPage] = useState(currentPage);

  // Complic chat state
  const [showComplicChat, setShowComplicChat] = useState(false);
  const [isVoiceActive, setIsVoiceActive] = useState(false);
  const [voiceState, setVoiceState] = useState({ isListening: false, isSpeaking: false, isProcessing: false });
  const [wakeWordDetected, setWakeWordDetected] = useState(false);
  const complicChatRef = React.useRef<any>(null);

  // Code panel state
  const [showCodePanel, setShowCodePanel] = useState(false);
  const [showBrowserPanel, setShowBrowserPanel] = useState(false);
  const [codeFolder, setCodeFolder] = useState<string | null>(null);
  const [codeLabel, setCodeLabel] = useState<string | null>(null);

  // Poll voice state periodically when voice is active
  React.useEffect(() => {
    if (!isVoiceActive || !complicChatRef.current) return;

    const interval = setInterval(() => {
      if (complicChatRef.current?.getVoiceState) {
        const state = complicChatRef.current.getVoiceState();
        // Derive isProcessing: not listening, not speaking, but voice is active
        const isProcessing = isVoiceActive && !state.isListening && !state.isSpeaking;
        setVoiceState({ ...state, isProcessing });
      }
    }, 200); // Update every 200ms

    return () => clearInterval(interval);
  }, [isVoiceActive]);

  // Open agent/ticket conversation from Agents panel or Complic tools
  React.useEffect(() => {
    const handler = async (ev: Event) => {
      const detail = (ev as CustomEvent).detail || {};
      setShowComplicChat(true);
      // Same ComplicChat instance — wait a tick for layout, then switch conversation
      const open = async (attempt = 0): Promise<void> => {
        await new Promise((r) => setTimeout(r, attempt === 0 ? 30 : 120));
        const ref = complicChatRef.current;
        if (!ref) {
          if (attempt < 8) return open(attempt + 1);
          return;
        }
        if (detail.conversationId && ref.openConversation) {
          await ref.openConversation(detail.conversationId);
        } else if (detail.ticketKey && ref.openTicketConversation) {
          await ref.openTicketConversation(detail.ticketKey);
        }
      };
      await open();
    };
    window.addEventListener('complic-open-conversation', handler as EventListener);
    return () => window.removeEventListener('complic-open-conversation', handler as EventListener);
  }, []);

  useEffect(() => {
    const handler = async (e: Event) => {
      const runId = (e as CustomEvent).detail?.runId as string | undefined;
      if (!runId) return;
      try {
        const { resolveAgentVscodeFolder } = await import('./utils/openAgentVscode');
        const data = await resolveAgentVscodeFolder(runId);
        setCodeFolder(data.folder);
        setCodeLabel(data.branch || null);
        setShowBrowserPanel(false);
        setShowCodePanel(true);
      } catch (err: any) {
        const msg =
          err?.response?.data?.detail ||
          err?.message ||
          'Nepodařilo se otevřít workspace pro tento run';
        console.error(err);
        window.alert(msg);
      }
    };
    window.addEventListener('complic-open-vscode', handler as EventListener);
    return () => window.removeEventListener('complic-open-vscode', handler as EventListener);
  }, []);

  useEffect(() => {
    const handler = () => {
      setShowCodePanel(false);
      setShowBrowserPanel(true);
    };
    window.addEventListener('complic-open-browser', handler);
    return () => window.removeEventListener('complic-open-browser', handler);
  }, []);

  // Render page content based on page type
  const renderPageContent = (page: PageType) => {
    switch (page) {
      case 'settings':
        return (
          <div className="p-8 h-full overflow-auto">
            <Settings />
          </div>
        );
      case 'dashboard':
        return (
          <div className="h-full overflow-auto">
            <ComplicDashboard />
          </div>
        );
      case 'agents':
      case 'jobs': // legacy localStorage page id
        return (
          <div className="h-full overflow-auto">
            <Agents
              onOpenAgentChat={async (ticketKey) => {
                window.dispatchEvent(
                  new CustomEvent('complic-open-conversation', {
                    detail: { ticketKey },
                  })
                );
              }}
            />
          </div>
        );
      case 'worklog':
      case 'tickets': // legacy — ticket UI lives in Worklog
        return (
          <div className="p-8 h-full overflow-auto">
            <Worklog
              onStartComplic={startDeveloper}
              openRequest={pendingTicketOpen}
              onOpenRequestHandled={() => setPendingTicketOpen(null)}
            />
          </div>
        );
      default:
        return (
          <div className="p-8 h-full overflow-auto">
            <Worklog
              onStartComplic={startDeveloper}
              openRequest={pendingTicketOpen}
              onOpenRequestHandled={() => setPendingTicketOpen(null)}
            />
          </div>
        );
    }
  };

  // Validate stored JWT on mount — stale tokens used to leave an empty UI (all APIs 401)
  useEffect(() => {
    let cancelled = false;

    const boot = async () => {
      const token = localStorage.getItem('access_token');
      const savedUser = localStorage.getItem('user');

      if (!token || !savedUser) {
        if (!cancelled) {
          clearAuthSession();
          setIsAuthenticated(false);
          setUser(null);
          setLoading(false);
        }
        return;
      }

      applyAuthToken(token);
      try {
        const me = await axios.get(`${API_URL}/api/auth/me`);
        if (cancelled) return;
        setUser(me.data);
        localStorage.setItem('user', JSON.stringify(me.data));
        setIsAuthenticated(true);
        // Sync HttpOnly cookie for VS Code proxy (page reload / existing session)
        axios
          .post(`${API_URL}/api/auth/session-cookie`, {}, { withCredentials: true })
          .catch(() => {
            /* cookie sync is best-effort */
          });
      } catch {
        if (cancelled) return;
        clearAuthSession();
        setIsAuthenticated(false);
        setUser(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void boot();
    return () => {
      cancelled = true;
    };
  }, []);

  // Save current page to localStorage whenever it changes
  useEffect(() => {
    const page = currentPage === 'tickets' ? 'worklog' : currentPage === 'jobs' ? 'agents' : currentPage;
    localStorage.setItem('complic_current_page', page);
  }, [currentPage]);

  useEffect(() => {
    if (isAuthenticated) {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const startDeveloper = async (ticketKey: string, mode: string = 'full') => {
    try {
      setPendingTicketOpen({ ticketKey, section: 'agent' });
      setCurrentPage('worklog');
      setShowComplicChat(true);
      if (complicChatRef.current?.openTicketConversation) {
        await complicChatRef.current.openTicketConversation(ticketKey);
      }
      let projectId: number | undefined;
      for (let i = 0; i < sessionStorage.length; i++) {
        const k = sessionStorage.key(i);
        if (k && k.startsWith('complic_selected_project_')) {
          const v = sessionStorage.getItem(k);
          if (v) {
            projectId = parseInt(v, 10);
            break;
          }
        }
      }
      await axios.post(`${API_URL}/api/agents/start`, {
        agent_id: 'developer',
        ticket_key: ticketKey,
        project_id: projectId || 1,
        mode,
        goal: `Developer ${mode} on ${ticketKey}`,
        open_conversation: true,
      });
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message);
      console.error('Error starting developer agent:', err);
    }
  };

  const handleNavigate = (page: PageType) => {
    if (page === currentPage || isTransitioning) return; // Don't animate if same page or already transitioning

    const pages: PageType[] = ['dashboard', 'worklog', 'agents', 'settings'];
    const norm = (p: PageType) =>
      p === 'tickets' ? 'worklog' : p === 'jobs' ? 'agents' : p;
    const currentIndex = pages.indexOf(norm(currentPage === 'jobs' as any ? 'agents' : currentPage));
    const newIndex = pages.indexOf(norm(page));

    // Determine direction
    const isForward = newIndex > currentIndex;

    // Set transitioning state and update page
    setIsTransitioning(true);
    setPreviousPage(currentPage);
    setCurrentPage(page === 'tickets' ? 'worklog' : page);
    setAnimationClass(isForward ? 'forward' : 'backward');

    // End transition after animation completes
    setTimeout(() => {
      setIsTransitioning(false);
      setAnimationClass('');
      setPreviousPage(page === 'tickets' ? 'worklog' : page);
    }, 300); // Match CSS animation duration
  };

  const handleLoginSuccess = (userData: any, token: string) => {
    setIsAuthenticated(true);
    setUser(userData);
    applyAuthToken(token);
  };

  const handleLogout = () => {
    axios
      .post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true })
      .catch(() => undefined);
    clearAuthSession();
    setIsAuthenticated(false);
    setUser(null);
  };

  useEffect(() => {
    if (!isAuthenticated) {
      document.documentElement.classList.remove('dark');
    }
  }, [isAuthenticated]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center relative">
        <div className="app-atmosphere" aria-hidden />
        <div className="relative text-slate-500 text-sm font-medium">Checking session…</div>
      </div>
    );
  }

  // Show login if not authenticated
  if (!isAuthenticated) {
    return <Login onLoginSuccess={handleLoginSuccess} />;
  }

  return (
    <ThemeProvider>
      <VoiceProvider>
        <ProjectProvider>
        <div className="h-screen relative flex flex-col overflow-hidden bg-transparent">
        <div className="app-atmosphere" aria-hidden />

        {/* Code Panel */}
        <CodePanel
          isOpen={showCodePanel}
          onClose={() => setShowCodePanel(false)}
          folder={codeFolder}
          label={codeLabel}
        />
        <BrowserPanel
          isOpen={showBrowserPanel}
          onClose={() => setShowBrowserPanel(false)}
        />

        {/* Code Toggle Handle - vertical button on left side */}
        <CodeToggleHandle
          isOpen={showCodePanel}
          onClick={() => {
            if (!showCodePanel) {
              setCodeFolder(null);
              setCodeLabel(null);
              setShowBrowserPanel(false);
            }
            setShowCodePanel(!showCodePanel);
          }}
        />
        <button
          type="button"
          onClick={() => {
            if (!showBrowserPanel) {
              setShowCodePanel(false);
            }
            setShowBrowserPanel(!showBrowserPanel);
          }}
          title={showBrowserPanel ? 'Close PM UI' : 'Open PM UI'}
          className={`
            fixed left-0 z-50
            px-1.5 py-3 rounded-r-md font-sans font-medium text-xs
            border-r border-t border-b bg-transparent
            ${showBrowserPanel
              ? 'border-emerald-400/60 text-emerald-400'
              : 'border-gray-400/40 text-gray-500 dark:text-gray-400'}
          `}
          style={{
            writingMode: 'vertical-rl',
            textOrientation: 'mixed',
            top: 'calc(50% + 4.5rem)',
            transform: 'translateY(-50%)',
          }}
        >
          PM UI
        </button>

        {/* Main content wrapper - shifts when left panels open */}
        <div
          className={`
            h-screen flex flex-col transition-all duration-300 ease-in-out relative
            ${showCodePanel || showBrowserPanel ? 'lg:ml-[66.666667%] lg:w-[33.333333%]' : 'ml-0 w-full'}
          `}
        >
          <Navbar
            onNavigate={handleNavigate}
            currentPage={currentPage}
            user={user}
            onLogout={handleLogout}
            onToggleCode={() => {
              if (!showCodePanel) setShowBrowserPanel(false);
              setShowCodePanel(!showCodePanel);
            }}
            isCodePanelOpen={showCodePanel}
          />

          <div className="px-4 md:px-6 pt-3 relative z-30">
            <JiraRateLimitBanner />
          </div>

      <div className="flex-1 overflow-hidden relative z-10 flex">
        <main className={`${showComplicChat ? 'flex-1 min-w-0' : 'w-full'} overflow-hidden`}>
          {error && (
            <div className="mx-6 mt-6 bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-400 rounded-lg px-6 py-4 relative z-20">
              <div className="flex items-center gap-3">
                <span className="text-red-600 dark:text-red-400 text-2xl">⚠</span>
                <div>
                  <p className="font-sans text-red-600 dark:text-red-400 font-bold">System Error</p>
                  <p className="font-sans text-sm text-red-700 dark:text-red-400/80 mt-1">{error}</p>
                </div>
              </div>
            </div>
          )}

          <div className="page-container">
            {/* Previous page - exit animation */}
            {isTransitioning && (
              <div className={`page-wrapper ${animationClass === 'forward' ? 'page-exit-left' : 'page-exit-right'}`}>
                {renderPageContent(previousPage)}
              </div>
            )}

            {/* Current page - enter animation */}
            <div className={`page-wrapper ${isTransitioning ? (animationClass === 'forward' ? 'page-enter-right' : 'page-enter-left') : ''}`}>
              {renderPageContent(currentPage)}
            </div>
          </div>
        </main>

        {/* One ComplicChat instance — hidden when closed so open_agent_session can switch without remount race */}
        <aside
          className={`
            border-l border-slate-200/80 dark:border-slate-700 bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl shadow-panel
            ${showComplicChat ? 'w-[min(36rem,40vw)] max-w-[40vw]' : 'w-0 max-w-0 overflow-hidden border-0 shadow-none'}
          `}
          aria-hidden={!showComplicChat}
        >
          <div className={showComplicChat ? 'h-full w-full min-w-0' : 'h-full w-[min(36rem,40vw)]'}>
            <ComplicChat
              ref={complicChatRef}
              onClose={() => {
                setShowComplicChat(false);
                setIsVoiceActive(false);
              }}
              onOpenSettings={() => {
                setShowComplicChat(false);
                setIsVoiceActive(false);
                setCurrentPage('settings');
              }}
              onOpenTicket={(ticketKey, section) => {
                complicChatRef.current?.openTicketConversation?.(ticketKey);
                setPendingTicketOpen({ ticketKey, section: section || null });
                setCurrentPage('worklog');
              }}
              onWakeWordActivated={() => {
                setIsVoiceActive(true);
                setShowComplicChat(true);
                complicChatRef.current?.startVoice?.();
              }}
              onWakeWordDetected={(detected: boolean) => {
                setWakeWordDetected(detected);
              }}
              onVoiceTimeout={() => {
                setIsVoiceActive(false);
                setVoiceState({ isListening: false, isSpeaking: false, isProcessing: false });
                complicChatRef.current?.stopVoice?.();
              }}
            />
          </div>
        </aside>
      </div>

        {/* Floating Complic Assistant — always bottom-right */}
        <FloatingComplic
          currentPage={currentPage}
          onOpenChat={() => setShowComplicChat(true)}
          isListening={voiceState.isListening}
          isSpeaking={voiceState.isSpeaking}
          isProcessing={voiceState.isProcessing}
          codePanelOpen={showCodePanel || showBrowserPanel}
          chatPanelOpen={showComplicChat}
        />

        {/* Floating Voice Assistant - always bottom right of viewport */}
        <FloatingVoiceAssistant
          onOpenChat={() => setShowComplicChat(true)}
          onStartVoice={() => {
            if (isVoiceActive) {
              setIsVoiceActive(false);
              setVoiceState({ isListening: false, isSpeaking: false, isProcessing: false });
              complicChatRef.current?.stopVoice?.();
            } else {
              setIsVoiceActive(true);
              setShowComplicChat(true);
              setTimeout(() => {
                complicChatRef.current?.startVoice?.();
              }, 200);
            }
          }}
          isVoiceActive={isVoiceActive}
          isListening={voiceState.isListening}
          isSpeaking={voiceState.isSpeaking}
          codePanelOpen={showCodePanel || showBrowserPanel}
          chatPanelOpen={showComplicChat}
        />
        </div>
      </div>
      </ProjectProvider>
      </VoiceProvider>
    </ThemeProvider>
  );
}

export default App;
